import I18n from 'components/I18n'
import BankIcon from 'components/Icons/Bank'
import ClockCircle from 'components/Icons/ClockCircle'
import CloseCircle from 'components/Icons/CloseCircle'
import InformationIcon from 'components/Icons/Information'
import palette from 'components/palette'
import messages from 'lang'
import React from 'react'
import { injectIntl, IntlShape, WrappedComponentProps } from 'react-intl'
import { getSupportLink } from 'services/urlService'
import styled from 'styled-components'

import { Button, Description, ImageWrapper, Title } from './styles'

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const ContentTopWrapper = styled.div`
  display: grid;
  grid-auto-rows: min-content;
  gap: 1.5rem;
  justify-items: center;
`

const NoMarginImgWrapper = styled(ImageWrapper)`
  margin-bottom: 0;
`

type Props = {
  error?: string
  onBack: () => void
} & WrappedComponentProps

const getErrorTitle = (error: string | undefined) => {
  switch (error) {
    case 'BANK_TRANSFER_ACCOUNT_ALREADY_LINKED':
      return <I18n data={messages.link_bank_error_account_already_linked} />
    case 'BANK_TRANSFER_ACCOUNT_EXPIRED':
    case 'BANK_TRANSFER_PAYMENT_EXPIRED':
      return <I18n data={messages.link_bank_error_account_expired} />
    case 'BANK_TRANSFER_ACCOUNT_FAILED':
    case 'BANK_TRANSFER_PAYMENT_FAILED':
      return <I18n data={messages.link_bank_error_failed_title} />
    case 'BANK_TRANSFER_ACCOUNT_NAME_MISMATCH':
      return <I18n data={messages.link_bank_error_name_title} />
    case 'BANK_TRANSFER_ACCOUNT_REJECTED':
      return <I18n data={messages.link_bank_error_rejected_title} />
    case 'BANK_TRANSFER_PAYMENT_DECLINED':
      return <I18n data={messages.link_bank_error_payment_declined} />
    case 'BANK_TRANSFER_PAYMENT_INVALID':
      return <I18n data={messages.label_deposit_failed} />
    case 'BANK_TRANSFER_PAYMENT_REJECTED':
      return <I18n data={messages.label_deposit_failed} />
    case 'BANK_TRANSFER_PAYMENT_LIMITED_EXCEEDED':
      return <I18n data={messages.label_deposit_failed} />
    case 'BANK_TRANSFER_PAYMENT_INSUFFICIENT_FUNDS':
      return <I18n data={messages.label_deposit_failed} />
    case 'MANUAL_REVIEW':
      return <I18n data={messages.label_deposit_pending} />
    case 'TIMEOUT':
      return <I18n data={messages.label_deposit_pending} />
    default:
      return <I18n data={messages.generic_data_error} />
  }
}
const getErrorDescription = (error: string | undefined, intl: IntlShape) => {
  const link = (
    <a href={getSupportLink(intl.locale)} rel='noopener noreferrer' target='_blank'>
      <I18n data={messages.contact_support} />
    </a>
  )
  switch (error) {
    case 'BANK_TRANSFER_ACCOUNT_ALREADY_LINKED':
    case 'BANK_TRANSFER_ACCOUNT_NAME_MISMATCH':
      return (
        <I18n
          data={messages.link_bank_error_name_subtitle}
          link={
            <a href={getSupportLink(intl.locale)} rel='noopener noreferrer' target='_blank'>
              <I18n data={messages.learn_more} />
            </a>
          }
        />
      )
    case 'BANK_TRANSFER_ACCOUNT_REJECTED':
      return <I18n data={messages.link_bank_error_rejected_subtitle} link={link} />
    case 'BANK_TRANSFER_PAYMENT_INVALID':
      return <I18n data={messages.link_bank_error_bank_transfer_payment_invalid_desc} />
    case 'BANK_TRANSFER_PAYMENT_REJECTED':
      return <I18n data={messages.link_bank_error_bank_transfer_payment_rejected_desc} />
    case 'BANK_TRANSFER_PAYMENT_LIMITED_EXCEEDED':
      return <I18n data={messages.link_bank_error_bank_transfer_payment_limited_exceeded_desc} />
    case 'BANK_TRANSFER_PAYMENT_INSUFFICIENT_FUNDS':
      return <I18n data={messages.link_bank_error_bank_transfer_payment_insufficient_funds_desc} />
    case 'MANUAL_REVIEW':
      return <I18n data={messages.link_bank_error_manual_review_desc} />
    case 'TIMEOUT':
      return <I18n data={messages.link_bank_error_timeout_desc} />
    default:
      return <I18n data={messages.link_bank_try_again} link={link} />
  }
}
const getIcon = (error: string | undefined) => {
  switch (error) {
    case 'BANK_TRANSFER_ACCOUNT_EXPIRED':
    case 'BANK_TRANSFER_PAYMENT_EXPIRED':
      return <ClockCircle fill={palette.orange500} size='32' />
    case 'BANK_TRANSFER_ACCOUNT_REJECTED':
    case 'BANK_TRANSFER_PAYMENT_REJECTED':
      return <CloseCircle fill={palette.orange500} size='32' />
    default:
      return <InformationIcon fill={palette.orange500} size='32' />
  }
}

const GeneralError = ({ error, intl, onBack }: Props) => (
  <StyledWrapper>
    <ContentTopWrapper>
      <NoMarginImgWrapper>
        <BankIcon fill={palette.primary} size='72' />
        {getIcon(error)}
      </NoMarginImgWrapper>
      <Title>{getErrorTitle(error)}</Title>
      <Description>{getErrorDescription(error, intl)}</Description>
    </ContentTopWrapper>
    <Button onClick={onBack}>
      <I18n data={messages.try_different_bank} />
    </Button>
  </StyledWrapper>
)

export default injectIntl(GeneralError)
